// Books and authors: the app's library on the web (biblestandard-api docs/fellowship/09-BOOKS-AND-AUTHORS.md).
// Built by scripts/build-books.mjs from the iOS app's registry.json and .bslib packages into
// public/library/: the catalogue, the 31 registry authors, every work's contents (titles and uids), and
// the TEXT of the rights-cleared free works only (scripts/library-allow.json). Static, fetched once and
// kept by Query, the same on the prototype and the API: the API holds references, never a book.
//
// A section is addressed as {libraryId, sectionUid}: the uid is the package's stable name for it
// (FS-039 D2). The section helpers here walk the contents exactly as the API's src/lib/library.ts
// does, so a plan made on the prototype reads the same days as one made on the API.
import { useQuery } from "@tanstack/react-query";
import { useLibrarySource, type ApiAuthor, type LibraryEntry, type LibrarySource } from "./catalogueSource";

export type Access = "free" | "paid";
export type Work = {
  id: string;
  title: string;
  shortTitle: string;
  kind: string;
  author: string;
  authorIds: string[];
  access: Access;
  written: string | null;
  language: string | null;
  set: { id: string; index: number } | null;
  /** Rows with content in the package. */
  sections: number;
  /** Reader pages (a confession's chapter with its paragraphs is one page). */
  pages: number;
  /** True only for rights-cleared free works whose text the web may show. */
  text: boolean;
  licence: string | null;
  rightsStatus: string | null;
  edition: string | null;
  /** From the API (W2): whether its rights are cleared, whether it can be opened at all, what unlocks it. */
  rights: "cleared" | "pending";
  available: boolean;
  productIds: LibraryEntry["productIds"];
  /** The cover image, or null for a lettered plate. */
  cover: string | null;
  /** "jacket" (FS-362) or null for the kind's binding. */
  binding: string | null;
  /** Its length in words (FS-371 D2: what its spine is thick by), or null. */
  words: number | null;
  /** The spine's own title (FS-371 D3), or null for the colon rule. */
  spineTitle: string | null;
  /** The catalogue entry itself (for its package). */
  entry: LibraryEntry;
};
export type Catalogue = { authorFacets: string[]; sets: { id: string; title: string; author: string; members: string[] }[]; works: Work[]; bibles: LibraryEntry[] };

export type Author = {
  id: string;
  name: string;
  dates: string | null;
  bio: string | null;
  facets: string[];
  /** The plate's ground: the registry's tone, or a house tone where it gives none. */
  tone: string;
  /** Portraits are unverified (FS-060): always null on the web, so a plate is drawn. */
  portrait: null;
  /** A collection label ("Various (Ante-Nicene Fathers)"), not a person. */
  collection: boolean;
  /** Whole works, or (the Fathers) their sections inside a volume. */
  works: { libraryId: string; sections?: string[] }[];
};

/** A contents row: section uid (null for a heading without content), title, nav role, parent row (-1 at the top), reader page (-1 for none). */
export type TocRow = [uid: string | null, title: string, role: string | null, parent: number, page: number];
export type Toc = { id: string; title: string; rows: TocRow[]; /** each page's first row */ pages: number[] };

export type Run = string | { text?: string; b?: boolean; i?: boolean; ref?: string; fn?: number };
export type Block = {
  t: "p" | "h" | "bq" | "ul" | "ol" | "table";
  runs?: Run[];
  level?: number;
  align?: string;
  role?: string;
  lines?: boolean;
  items?: (Run[] | { runs?: Run[] })[];
  rows?: (Run[] | { runs?: Run[] })[][];
};
export type Note = { id: number; marker: string; kind: string; blocks: Block[] };
export type Part = { uid: string; title: string; role: string | null; blocks: Block[]; notes: Note[] };
export type Page = { libraryId: string; page: number; uid: string; title: string; parent: string | null; parts: Part[]; licence: string | null; source: string | null; from: string };
export type Quote = { text: string; author: string; authorId: string; work: string; libraryId: string; sectionUid: string | null };

/** A section reading in a plan, as the API's F7 PlanSectionReading. */
export type SectionReading = { kind: "section"; key: string; label: string; libraryId: string; sectionUid: string; workTitle: string };

// ---- The contents, as the API walks them ----

/** Rows that frame a book rather than read in it: left out when a whole work is spread over a plan (the API's FRAME_ROLES). */
export const FRAME_ROLES = new Set(["titlePage", "frontMatter", "contents", "backMatter"]);

export type Section = { uid: string; title: string; role: string | null; label: string; index: number };

/** "Mortification of Sin in Believers › Chapter 3": the section under its parent heading (never the title page). As the API's sectionAt. */
export function sectionAt(toc: Pick<Toc, "rows">, i: number): Section {
  const [uid, title, role, parent] = toc.rows[i]!;
  const up = parent >= 0 ? toc.rows[parent]! : null;
  const label = up && up[2] !== "titlePage" ? `${up[1]} › ${title}` : title;
  return { uid: uid!, title, role, label, index: i };
}

export function findSection(toc: Pick<Toc, "rows">, uid: string): Section | undefined {
  const i = toc.rows.findIndex((r) => r[0] === uid);
  return i < 0 ? undefined : sectionAt(toc, i);
}

/** The sections a plan reads through a work, in contents order: the uids given, or the leaves (every readable section that holds no other, never the frame). As the API's readingSections. */
export function readingSections(toc: Pick<Toc, "rows">, uids?: string[]): Section[] | undefined {
  if (uids) {
    const at = [...new Set(uids)].map((u) => toc.rows.findIndex((r) => r[0] === u));
    if (at.some((i) => i < 0)) return undefined;
    return at.sort((a, b) => a - b).map((i) => sectionAt(toc, i));
  }
  // A section that holds others is read through them, never as well as them.
  const readable = (r: TocRow) => Boolean(r[0]) && !FRAME_ROLES.has(r[2] ?? "");
  const holders = new Set<number>();
  toc.rows.forEach((r) => {
    if (!readable(r)) return;
    for (let p = r[3]; p >= 0 && !holders.has(p); p = toc.rows[p]![3]) holders.add(p);
  });
  return toc.rows.flatMap((r, i) => (readable(r) && !holders.has(i) ? [sectionAt(toc, i)] : []));
}

/** Section readings for a plan, keyed `b:<libraryId>/<sectionUid>`, labelled "<short title> › <section>". As the API's alongsideReadings. */
export function sectionReadings(work: Pick<Work, "id" | "title" | "shortTitle">, toc: Pick<Toc, "rows">, uids?: string[]): SectionReading[] {
  return (readingSections(toc, uids) ?? []).map((s) => ({
    kind: "section",
    key: `b:${work.id}/${s.uid}`,
    label: `${work.shortTitle} › ${s.label}`,
    libraryId: work.id,
    sectionUid: s.uid,
    workTitle: work.title,
  }));
}

/** The reader pages that read in a work (not its title page, front or back matter): what "the whole book" means in the plan builder. */
export function pageSections(toc: Toc): Section[] {
  return toc.pages.map((i) => sectionAt(toc, i)).filter((s) => !FRAME_ROLES.has(s.role ?? ""));
}

/** The reader page a section is read on, or -1. */
export const pageOf = (toc: Pick<Toc, "rows">, uid: string) => toc.rows.find((r) => r[0] === uid)?.[4] ?? -1;

/** The section's own words after the work's short title, for a row that shows the work's title above it. */
export const sectionPart = (r: Pick<SectionReading, "label">) => r.label.split(" › ").slice(1).join(" › ") || r.label;

/** How a work can be read here: on this site, free in the app, or in the app (paid). Never a price, lock or badge (D5). */
export type Availability = "web" | "app-free" | "app-paid";
export const availability = (w: Pick<Work, "text" | "access">): Availability => (w.text ? "web" : w.access === "free" ? "app-free" : "app-paid");
export const AVAILABILITY_LINE: Record<Availability, string> = {
  web: "Read it here",
  "app-free": "Free in the Bible Standard app",
  "app-paid": "In the Bible Standard app",
};

export const KIND_LABEL: Record<string, string> = { book: "Book", catechism: "Catechism", confession: "Confession", commentary: "Commentary" };

/** Plain text of blocks, for a quote or an excerpt (footnote markers left out). */
export function blocksText(blocks: Block[]): string {
  const run = (r: Run) => (typeof r === "string" ? r : r.fn !== undefined ? "" : r.ref && !r.text ? r.ref : (r.text ?? r.ref ?? ""));
  const runs = (rs: Run[] | { runs?: Run[] } | undefined) => (Array.isArray(rs) ? rs : (rs?.runs ?? [])).map(run).join("");
  return blocks
    .map((b) => [runs(b.runs), ...(b.items ?? []).map(runs)].join(" "))
    .join("\n\n")
    .replace(/[ \t]+/g, " ")
    .trim();
}

/** The tone of a work: its first author's registry tone, else undefined (the cover picks a stable one). */
export function toneOf(work: Pick<Work, "authorIds"> | null | undefined, authors: Author[] | undefined): string | undefined {
  const id = work?.authorIds[0];
  return authors?.find((a) => a.id === id)?.tone;
}

/** The era group an author is shown under on /authors (from the registry's facets). */
export const ERAS = [
  { key: "Fathers", label: "The early church" },
  { key: "Medieval", label: "The medieval church" },
  { key: "Reformers", label: "The Reformation" },
  { key: "Puritans", label: "The Puritans" },
  { key: "Later", label: "Since the Puritans" },
] as const;
export const eraOf = (a: Pick<Author, "facets">) => ERAS.find((e) => a.facets.includes(e.key))?.key ?? "Later";

// ---- Loading ----
//
// The catalogue and authors come from the library source: GET /v1/library and /v1/authors on the API,
// the static files on the prototype. The static files built from the packages (public/library) still
// give what the API doesn't serve: every work's contents (toc/), the Fathers' own sections inside the
// ANF volumes, licence lines, and the text of the cleared works for the prototype and offline.

async function get<T>(path: string): Promise<T> {
  const base = (import.meta.env?.BASE_URL as string | undefined) ?? "/";
  const r = await fetch(`${base}library/${path}`);
  if (!r.ok) throw new Error("This part of the library isn’t available just now.");
  return (await r.json()) as T;
}

/** The static catalogue as scripts/build-books.mjs wrote it (the prototype's own, and the plans mock's). */
export const loadCatalogue = () => get<Omit<Catalogue, "bibles">>("catalogue.json");
export const loadToc = (id: string) => get<Toc>(`toc/${encodeURIComponent(id)}.json`);
const safeId = (id: string) => /^[A-Za-z0-9][A-Za-z0-9._-]{0,99}$/.test(id);

type StaticWork = Omit<Work, "rights" | "available" | "productIds" | "cover" | "entry">;

/** A catalogue entry as the app's Work: the API's fields, with the static file's contents counts and licence where it has them. */
export function toWork(e: LibraryEntry, st: StaticWork | undefined, source: Pick<LibrarySource, "live" | "coverUrl">): Work {
  return {
    id: e.id,
    title: e.title,
    shortTitle: e.shortTitle || e.title,
    kind: e.kind,
    author: e.author ?? st?.author ?? "",
    authorIds: e.authorIds.length ? e.authorIds : (st?.authorIds ?? []),
    access: e.access,
    written: e.year ?? st?.written ?? null,
    language: e.language ?? st?.language ?? null,
    set: st?.set ?? null,
    sections: st?.sections ?? 0,
    pages: st?.pages ?? 0,
    // On the API every free, available work is read from its package; on the prototype, only the allowlisted text.
    text: source.live ? e.available && e.access === "free" : (st?.text ?? false),
    licence: st?.licence ?? null,
    rightsStatus: st?.rightsStatus ?? null,
    edition: st?.edition ?? null,
    rights: e.rights,
    available: e.available,
    productIds: e.productIds,
    cover: source.coverUrl(e),
    binding: e.binding ?? null,
    words: e.words ?? null,
    spineTitle: e.spineTitle ?? null,
    entry: e,
  };
}

export async function loadCatalogueFrom(source: LibrarySource): Promise<Catalogue> {
  const [entries, statics] = await Promise.all([source.entries(), loadCatalogue().catch(() => null)]);
  const st = new Map((statics?.works ?? []).map((w) => [w.id, w as StaticWork]));
  return {
    authorFacets: statics?.authorFacets ?? [],
    sets: statics?.sets ?? [],
    works: entries.filter((e) => e.kind !== "bible" && e.kind !== "plan").map((e) => toWork(e, st.get(e.id), source)),
    bibles: entries.filter((e) => e.kind === "bible"),
  };
}

/** HOUSE tones for an author the registry gives none (as scripts/build-books.mjs picks them). */
const HOUSE_TONES = ["#2b2f3a", "#3a2a2a", "#24302a", "#33291f", "#2a2438", "#1f2d33", "#3a2f24", "#2c2c2c"];
const houseTone = (name: string) => HOUSE_TONES[[...name].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7) % HOUSE_TONES.length]!;

/** The API's authors as the app's, with the static file's shelves (the Fathers' sections) where it has them. */
export function toAuthor(a: ApiAuthor, st: Author | undefined): Author {
  const sections = new Map((st?.works ?? []).map((w) => [w.libraryId, w.sections]));
  return {
    id: a.id,
    name: a.name,
    dates: a.dates,
    bio: a.bio,
    facets: a.facets,
    tone: a.tone ?? st?.tone ?? houseTone(a.name),
    portrait: null,
    collection: st?.collection ?? /^Various|Churches|Assembly/.test(a.name),
    works: a.works.map((libraryId) => ({ libraryId, ...(sections.get(libraryId) ? { sections: sections.get(libraryId) } : {}) })),
  };
}

export async function loadAuthorsFrom(source: LibrarySource): Promise<Author[]> {
  const [list, statics] = await Promise.all([source.authors(), get<{ authors: Author[] }>("authors.json").catch(() => null)]);
  const st = new Map((statics?.authors ?? []).map((a) => [a.id, a]));
  return list.map((a) => toAuthor(a, st.get(a.id)));
}

export const worksKeys = {
  catalogue: ["works", "catalogue"] as const,
  authors: ["works", "authors"] as const,
  quotes: ["works", "quotes"] as const,
  toc: (id: string) => ["works", "toc", id] as const,
  page: (id: string, n: number) => ["works", "page", id, n] as const,
};

export const useCatalogue = () => {
  const s = useLibrarySource();
  return useQuery({ queryKey: worksKeys.catalogue, queryFn: () => loadCatalogueFrom(s), staleTime: 5 * 60_000 });
};
export const useAuthors = () => {
  const s = useLibrarySource();
  return useQuery({ queryKey: worksKeys.authors, queryFn: () => loadAuthorsFrom(s), staleTime: 60 * 60_000 });
};
export const useQuotes = () => useQuery({ queryKey: worksKeys.quotes, queryFn: () => get<{ quotes: Quote[] }>("quotes.json").then((x) => x.quotes), staleTime: Infinity });
export const useToc = (id: string | null | undefined) =>
  useQuery({ queryKey: worksKeys.toc(id ?? ""), queryFn: () => loadToc(id!), staleTime: Infinity, enabled: !!id && safeId(id) });
/** One reader page of a work, from the static text (the prototype, and offline). */
export const usePage = (id: string, n: number, enabled = true) =>
  useQuery({ queryKey: worksKeys.page(id, n), queryFn: () => get<Page>(`text/${encodeURIComponent(id)}/${n}.json`), staleTime: Infinity, enabled: enabled && safeId(id) && n >= 0 });
export const loadStaticPage = (id: string, n: number) => get<Page>(`text/${encodeURIComponent(id)}/${n}.json`);

/** The catalogue as a map, with lookups the pages share. */
export function useWork(id: string | null | undefined) {
  const { data, ...rest } = useCatalogue();
  return { ...rest, data: data && id ? (data.works.find((w) => w.id === id) ?? null) : undefined };
}
